/**
 * THE MIDPOINT TRINE SCAN, on the chart.
 *
 * <h2>Every mark sits on the RAIL, because the study knows only WHEN</h2>
 *
 * A midpoint trine is three bodies standing 120 apart. It carries no price, no level and no
 * direction — so a triangle above a bar's high would be making the short-signal claim the CTS
 * marks were moved off the candles for making, and a mark at the close would imply a level the
 * scan never computed. `anchor: 'bottom'` puts them all on the 0..1 rail at the foot of the grid,
 * which says "this bar" and nothing else.
 *
 * <p>The points therefore carry NO `price` at all. That is not an omission the renderer tolerates
 * by luck: `onRailFor` sends a point with no finite price to the rail by rule, so the absence IS
 * the instruction. A rail-anchored overlay is also exempt from the price-series filter, so a point
 * with no price is kept rather than dropped.
 *
 * <h2>A non-trading day has no column, and most of these fall on one</h2>
 *
 * The x axis is a CATEGORY axis over the `YYYY-MM-DD` strings in `barData.dates`. A Saturday is
 * not one of them, so handing the renderer that date makes ECharts drop the point in silence —
 * and the sky does not keep market hours, so roughly two in seven of these would vanish.
 *
 * <p>They are SNAPPED FORWARD to the next trading day, as the Deep Celestial Research marks are,
 * and the tooltip says so. Forward because that is how a weekend configuration is acted on: it
 * had already happened when the bar opened.
 *
 * <h2>A builder over the shared renderer, not a new one</h2>
 *
 * `chartDataProcessor`'s `pivotmarkers` branch already handles the rail axis, the per-point
 * tooltip card and the research-tooltip exemption. A second renderer here would duplicate all of
 * it and quietly opt out of the fixes those have accumulated.
 */

/** One id, so a re-run replaces the previous paint rather than stacking on it. */
export const MIDPOINT_TRINE_OVERLAY_ID = 'midpoint-trine-markers';

/**
 * A deep teal, chosen against everything else that can share this canvas: the Deep Celestial
 * indigo #4C5B8A, the CTS amber #FFC107, PTA #F59E0B, PTB #06B6D4, the Deep Pivot bronze #8B7355
 * and the yearly plaque's gold #D4A017.
 */
export const MIDPOINT_TRINE_COLOR = '#00796B';

/**
 * At most this many marks.
 *
 * <p>A century holds about a thousand configurations and ECharts lays out every markPoint on
 * every redraw. The cap keeps a wide chart responsive — and it is REPORTED by the caller rather
 * than applied in silence, because a truncated overlay reads as "this is all of them".
 */
export const MIDPOINT_TRINE_MAX_MARKS = 300;

/**
 * The index of the first trading day on or after a date, or -1 past the end.
 *
 * <p>`dates` is sorted and in `YYYY-MM-DD`, so a string comparison IS a date comparison. Parsing
 * would reintroduce the timezone shift this repository has already been caught by, to answer a
 * question that needs no clock.
 */
export function indexAtOrAfter(dates, day) {
  if (!Array.isArray(dates) || dates.length === 0 || !day) return -1;
  let lo = 0;
  let hi = dates.length - 1;
  let found = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (dates[mid] >= day) {
      found = mid;
      hi = mid - 1;
    } else {
      lo = mid + 1;
    }
  }
  return found;
}

const fixed = (v) => (Number.isFinite(v) ? v.toFixed(3) : '—');

/** The hover card. Says what was found, how tight it was, and when it was tightest. */
function tooltipLinesFor(hit, snappedFrom) {
  const lines = [];
  lines.push(`Exact at ${hit.instant || hit.date}`);
  const orbs = hit.trineOrbs || {};
  Object.keys(orbs).forEach((pair) => {
    lines.push(`${pair.replace('-', ' – ')}: ${fixed(orbs[pair])}° from an exact trine`);
  });
  // The "smack between" half of the brief, reported for ALL THREE because all three hold at once:
  // in a grand trine every body is the far midpoint of the other two, so nominating one would be
  // picking arbitrarily. See MidpointTrine.java.
  const offsets = hit.midpointOffsets || {};
  const offsetText = Object.keys(offsets)
    .map((b) => `${b} ${fixed(offsets[b])}°`)
    .join(', ');
  if (offsetText) lines.push(`Off its midpoint: ${offsetText}`);
  if (snappedFrom) {
    // Said explicitly. A mark on a bar whose date is not the configuration's date is the one
    // thing here a reader could reasonably take for a bug rather than a convention.
    lines.push(`Fell on ${snappedFrom}, a non-trading day — drawn on the next bar`);
  }
  return lines;
}

/**
 * Build the overlay.
 *
 * @param hits  the backend's `hits` array, each `{date, instant, label, bodies, worstOrb,
 *              trineOrbs, midpointOffsets}`
 * @param dates the chart's own x-axis categories, `YYYY-MM-DD`, ascending
 * @returns `{overlay, drawn, snapped, beyondChart, truncated}` — or `overlay: null` when nothing
 *          can be drawn, so the caller can say which of "none found" and "none on this chart" it
 *          is. Those are different findings and a bare empty overlay conflates them.
 */
export function buildMidpointTrineOverlay(hits, dates) {
  const rows = Array.isArray(hits) ? hits : [];
  let snapped = 0;
  let beyondChart = 0;

  const points = [];
  rows.forEach((hit) => {
    const day = hit && hit.date;
    if (!day) return;
    const idx = indexAtOrAfter(dates, day);
    if (idx === -1) {
      // Past the last bar. Dropped rather than clamped: clamping would pile every future
      // configuration onto the final column, which reads as a cluster that is not there.
      beyondChart += 1;
      return;
    }
    const barDate = dates[idx];
    const wasSnapped = barDate !== day;
    if (wasSnapped) snapped += 1;
    points.push({
      date: barDate,
      // NO price, deliberately — see the file note. This is what sends it to the rail.
      label: '',
      tooltipLines: tooltipLinesFor(hit, wasSnapped ? day : null),
      tooltipFooter: hit.label,
    });
  });

  const truncated = Math.max(0, points.length - MIDPOINT_TRINE_MAX_MARKS);
  // The MOST RECENT, not the first found. A reader looking at a chart is looking at its right
  // hand end, and keeping the oldest would empty exactly the stretch they are reading.
  const kept = truncated > 0 ? points.slice(points.length - MIDPOINT_TRINE_MAX_MARKS) : points;

  if (kept.length === 0) {
    return { overlay: null, drawn: 0, snapped, beyondChart, truncated: 0 };
  }

  return {
    overlay: {
      type: 'pivotMarkers',
      id: MIDPOINT_TRINE_OVERLAY_ID,
      points: kept,
      // Every mark on the rail. The renderer still decides per point, and with no price on any of
      // them that resolves to the rail for all of them — which is the claim this study makes.
      anchor: 'bottom',
      color: MIDPOINT_TRINE_COLOR,
      colorRole: 'midpointTrine',
      label: 'Midpoint trines',
      symbol: 'diamond',
      symbolSize: 9,
      // ADMIN RESEARCH, so the short-bar preset does not swallow the hover card. That preset is a
      // customer-facing constraint — nothing astrological reaches the screen — and this scan is
      // reachable only from a dialog that renders nothing for a non-admin, so there is no
      // customer to constrain. Without it the marks draw and answer no hover, which reads as
      // broken rather than as restricted.
      researchTooltip: true,
      source: 'midpointTrine',
    },
    drawn: kept.length,
    snapped,
    beyondChart,
    truncated,
  };
}

/** The one-line status a reader gets back. Every number it quotes is a decision, so none is silent. */
export function describeMidpointTrineScan({ total, drawn, snapped, beyondChart, truncated }) {
  if (!total) return 'Midpoint trine scan: no configurations within the orb over this range.';
  const parts = [`Midpoint trine scan: ${total} configuration${total === 1 ? '' : 's'}`];
  parts.push(`${drawn} drawn`);
  if (snapped) parts.push(`${snapped} snapped forward from a non-trading day`);
  if (beyondChart) parts.push(`${beyondChart} past the last bar`);
  if (truncated) parts.push(`${truncated} older ones not drawn (cap ${MIDPOINT_TRINE_MAX_MARKS})`);
  return `${parts.join(' · ')}.`;
}
